import React, {FunctionComponent} from "react";
import {Box, Card, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import Dialog from "../../../../../../components/base/dialog";
import Button from "../../../../../../components/base/button/button";

export type PurchasePlanningSaveStatus = 'pending' | 'saving' | 'saved' | 'failed' | 'unavailable';

export interface PurchasePlanningSaveEntry {
    id: string;
    label: string;
    status: PurchasePlanningSaveStatus;
    detail?: string;
}

export interface PurchasePlanningSaveDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    saving: boolean;
    entries: PurchasePlanningSaveEntry[];
}

const statusColor: Record<PurchasePlanningSaveStatus, string> = {
    pending: 'text.secondary',
    saving: 'secondary.main',
    saved: 'primary.main',
    failed: 'error.main',
    unavailable: 'text.disabled',
};

const PurchasePlanningSaveDialog: FunctionComponent<PurchasePlanningSaveDialogProps> = (props) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchase-planning-details'});
    const statusLabel: Record<PurchasePlanningSaveStatus, string> = {
        pending: t($ => $.saveWaiting),
        saving: t($ => $.saving),
        saved: t($ => $.saveSaved),
        failed: t($ => $.saveFailed),
        unavailable: t($ => $.saveUnavailable),
    };

    return (
        <Dialog
            open={props.open}
            setOpen={props.setOpen}
            loading={props.saving}
            maxWidth={'sm'}
            fullWidth
            className={'bound-dialog'}
        >
            <DialogTitle component={'div'}>
                <Typography variant={'h6'} sx={{p: 2}}>{t($ => $.saveTitle)}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, p: 2}}>
                    {props.entries.map(entry => (
                        <Card
                            key={entry.id}
                            variant={'outlined'}
                            sx={{
                                boxShadow: 'none',
                                backgroundColor: 'background.paper',
                                borderColor: 'divider',
                                paddingBlock: 1,
                                paddingInline: 1.5,
                            }}
                        >
                            <Typography variant={'body2'} color={'text.primary'}>{entry.label}</Typography>
                            <Typography variant={'body2'} sx={{color: statusColor[entry.status]}}>
                                {statusLabel[entry.status]}
                            </Typography>
                            {
                                !!entry.detail &&
                                <Typography variant={'body2'} color={'text.secondary'}>{entry.detail}</Typography>
                            }
                        </Card>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    disabled={props.saving}
                    onClick={() => props.setOpen(false)}
                >
                    {t($ => $.close)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PurchasePlanningSaveDialog;
